import AddIcon from "@mui/icons-material/Add";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { DeleteIconList } from "@platformx/shared/static-assets";
import { ShowToastError } from "@platformx/utilities";
import { useCallback, useEffect, useState } from "react";
import { Control, FieldErrors, UseFormClearErrors, UseFormRegister } from "react-hook-form";
import builder from "../../builder";

interface RepeaterField {
  name: string;
  validations?: { type: string; value: string }[];
  options?: any[];
  defaultLength?: number;
  [key: string]: any;
}

interface RepeaterProps {
  field: RepeaterField;
  control: Control<any>;
  errors: FieldErrors<any>;
  register: UseFormRegister<any>;
  clearErrors: UseFormClearErrors<any>;
  state: any;
  setState: any;
  isEdit?: boolean;
  title?: string;
}

const DEFAULT_MAXIMUM_ITEMS = 10;

const CustomRepeater = ({
  field,
  control,
  errors,
  register,
  clearErrors,
  state,
  setState,
  isEdit,
  title,
}: RepeaterProps) => {
  const [localItems, setLocalItems] = useState<any[]>([]);
  const repeaterValidation = field.validations?.find((v: any) => v.type === "repeater");
  const maxItems = repeaterValidation
    ? parseInt(repeaterValidation.value, 10)
    : DEFAULT_MAXIMUM_ITEMS;
  const defaultLength = field.defaultLength || 0;
  const hasFixedLength = defaultLength > 0;
  const [usedIds, setUsedIds] = useState<Set<string>>(new Set());

  const generateUniqueId = useCallback(() => {
    let newId: string;
    do {
      const randomNumber = Math.floor(Math.random() * 90000) + 10000;
      newId = randomNumber.toString();
    } while (usedIds.has(newId));
    setUsedIds((prev) => new Set(prev).add(newId));
    return newId;
  }, [usedIds]);

  // `id` is a client-only tracking key (React list key + unique field-naming
  // suffix) — it must never be written into `state[field.name]`, since that
  // object is exactly what gets submitted to the backend, and the schema
  // doesn't declare an "id" field on repeater rows. The save/update endpoint
  // tolerates the extra key, but publish validates the stored document
  // strictly against the schema and rejects it as an unknown field.
  const stripId = (item: { [key: string]: any }) => {
    const { id: _id, ...rest } = item;
    return rest;
  };

  const addNewItem = () => {
    if (localItems?.length >= maxItems) {
      ShowToastError(`Maximum of ${maxItems} items allowed.`);
      return;
    }
    // Create a default object based on the options schema
    const newObject: { [key: string]: any } = {};
    field.options?.forEach((child: any) => {
      newObject[child.name] = "";
    });
    newObject["id"] = `${field.name}_${generateUniqueId()}`; // Local-only, for React keys/field naming
    setLocalItems([...localItems, newObject]);
    // Update external state to keep it synchronized
    if (state && typeof setState === "function") {
      setState({
        ...state,
        [field.name]: [...(state[field.name] || []), stripId(newObject)],
      });
    }
  };

  const removeItem = (id: string) => {
    // Prevent removing if fixed length or only one item
    if (hasFixedLength) {
      ShowToastError("Cannot remove items from a fixed length repeater.");
      return;
    }

    if (localItems?.length <= 1) {
      ShowToastError("At least one item is required.");
      return;
    }
    const removeIndex = localItems.findIndex((x) => x.id === id);
    setLocalItems(localItems.filter((x) => x.id !== id));
    // Update external state to keep it synchronized — state items don't carry
    // `id`, so remove by the matching position instead.
    if (state && typeof setState === "function" && removeIndex >= 0) {
      setState({
        ...state,
        [field.name]: (state[field.name] || []).filter((_y, index) => index !== removeIndex),
      });
    }
  };

  // Initialize default items
  useEffect(() => {
    if (state?.[field.name]?.length > 0) {
      // Existing rows loaded from the server never carry `id` — assign a
      // local-only one for React keys/field naming without writing it back.
      setLocalItems(
        state[field.name].map((item) => ({
          ...item,
          id: item.id || `${field.name}_${generateUniqueId()}`,
        })),
      );
    } else if (localItems?.length === 0) {
      const itemsToAdd = hasFixedLength ? defaultLength : 1;
      const defaultObjects: { [key: string]: any }[] = [];

      for (let i = 0; i < itemsToAdd; i++) {
        const defaultObject: { [key: string]: any } = {};
        field?.options?.forEach((child: any) => {
          defaultObject[child.name] = "";
        });
        defaultObject["id"] = `${field.name}_${generateUniqueId()}`; // Local-only
        defaultObjects.push(defaultObject);
      }
      setLocalItems([...defaultObjects]);
      if (defaultObjects.length > 0) {
        if (state && typeof setState === "function") {
          setState({
            ...state,
            [field.name]: defaultObjects.map(stripId),
          });
        }
      }
    }
  }, [field.options, hasFixedLength, defaultLength, field.name]);

  return (
    <Box
      sx={{
        border: "1px solid #D9DBE9",
        borderRadius: "5px",
        padding: "20px",
      }}
      id={`${field?.name}_id`}>
      {localItems?.map((item, index) => (
        <Box
          key={item.id}
          display='flex'
          flexDirection='column'
          mb={2}
          sx={{
            border: "1px solid #eee",
            borderRadius: 2,
            p: 2,
            position: "relative",
          }}>
          <Typography variant='p3regular' sx={{ color: "#01B2AD", marginTop: "0px" }}>
            {field.title} {index + 1}
          </Typography>
          {field.options?.map((childField: any) => {
            // Use proper react-hook-form array structure
            const nestedFieldName = `${field.name}[${index}].${childField.name}_${item.id}`;
            // Create a modified field object with the correct nested name
            const fieldForBuilder = {
              ...childField,
              id: `${childField.name}_${item.id}`, // Ensure unique ID for each child field
              name: nestedFieldName,
              repeaterName: field.name,
              repeaterIndex: index,
              childName: childField.name,
            };

            const nestedFieldErrors = errors[field.name]?.[index];

            return (
              <Box key={`${item.id}_${childField.id || childField.name}`} sx={{ mb: 3 }}>
                {builder(
                  fieldForBuilder,
                  state,
                  nestedFieldErrors,
                  register,
                  clearErrors,
                  setState,
                  field.options,
                  isEdit,
                  title,
                  control,
                  null,
                  true, //this is for refer inside repeater field
                )}
              </Box>
            );
          })}
          {!hasFixedLength && localItems?.length > 1 && (
            <IconButton
              aria-label='Delete row'
              onClick={() => removeItem(item.id)}
              sx={{ position: "absolute", top: 10, right: 11 }}
              size='small'>
              <img src={DeleteIconList} alt='delete' />
            </IconButton>
          )}
        </Box>
      ))}
      {!hasFixedLength && (
        <Button
          variant='primaryButton1'
          startIcon={<AddIcon />}
          onClick={addNewItem}
          disabled={localItems?.length >= maxItems}
          sx={{ mt: 1, padding: "9px 16px", fontSize: "14px", height: "38px", minWidth: "auto" }}>
          Add Another Group
        </Button>
      )}
      {!hasFixedLength && (
        <Typography variant='caption' color='text.secondary'>
          {`Maximum ${maxItems} ${maxItems > 1 ? "items" : "item"} allowed`}
        </Typography>
      )}
    </Box>
  );
};

export default CustomRepeater;
